import { useMemo } from "react";
import { formatMs } from "@/utils/format";
import {
  axisTicks,
  buildScale,
  laneTree,
  type NormalizedTrace,
  type TraceNode,
} from "@/utils/trace/model";
import { TraceLaneRow } from "./TraceLaneRow";
import { TraceNodeList } from "./TraceNodeList";
import { TraceSequence } from "./TraceSequence";

const TICK_COUNT = 4;

function LegendSwatch({ swatch, label }: { swatch: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={`inline-block h-2.5 w-2.5 rounded-sm ${swatch}`} />
      {label}
    </span>
  );
}

/**
 * Waterfall over a shared time axis, one lane per flow.
 *
 * The flows in a trace run concurrently — citation workers and sub-agents work
 * while the main loop waits on them — which is exactly what a line chart over
 * phase ordinals cannot show. Every node carries an absolute start and end
 * offset from the trigger message, so they can all be drawn against one axis.
 *
 * The spec describes this as columns on a vertical timeline; a horizontal
 * waterfall was chosen instead after comparing the two, on the usual
 * left-to-right tracing convention. The distinction the spec actually asks for
 * — "추론 중" vs "대기 중", and the wait window overlapping the other flows —
 * is preserved, just along the other axis.
 */
export function TraceTimeline({
  trace,
  selected,
  onSelect,
}: {
  trace: NormalizedTrace;
  selected: TraceNode | null;
  onSelect: (node: TraceNode) => void;
}) {
  const { firstStartMs, lastEndMs, answerSpanMs } = trace.timeline;
  // The axis bends at the moment the answer was published, when durable work
  // carried on long after it. See buildScale.
  const scale = useMemo(
    () =>
      buildScale({ startMs: firstStartMs, endMs: lastEndMs, breakMs: answerSpanMs }),
    [firstStartMs, lastEndMs, answerSpanMs],
  );

  // Hovering is not enough to link lanes reliably, so selection drives it:
  // picking a tool call highlights the lanes it spawned, and vice versa.
  const highlightedNodeIds = useMemo(() => {
    const ids = new Set<string>();
    if (!selected) return ids;
    for (const lane of trace.lanes) {
      if (lane.parentNodeId === selected.id) {
        for (const node of lane.nodes) ids.add(node.id);
      }
      if (lane.nodes.some((node) => node.id === selected.id) && lane.parentNodeId) {
        ids.add(lane.parentNodeId);
      }
    }
    return ids;
  }, [selected, trace.lanes]);

  // Shared with the token view so the two never disagree about the hierarchy.
  const ordered = useMemo(() => laneTree(trace.lanes), [trace.lanes]);

  // Siblings from one delegate_tasks call share a batch_id but are scattered
  // through `lanes`, because the server interleaves every flow by start offset.
  // Numbering them is the only way the relationship survives.
  const siblingLabels = useMemo(() => {
    const counts = new Map<string, number>();
    for (const lane of trace.lanes) {
      if (!lane.groupId) continue;
      counts.set(lane.groupId, (counts.get(lane.groupId) ?? 0) + 1);
    }
    const seen = new Map<string, number>();
    const labels = new Map<string, string>();
    for (const lane of trace.lanes) {
      const total = lane.groupId ? counts.get(lane.groupId) ?? 0 : 0;
      if (!lane.groupId || total < 2) continue;
      const index = (seen.get(lane.groupId) ?? 0) + 1;
      seen.set(lane.groupId, index);
      labels.set(lane.id, `batch ${index}/${total}`);
    }
    return labels;
  }, [trace.lanes]);

  const ticks = useMemo(
    () => axisTicks(scale, { startMs: firstStartMs, endMs: lastEndMs }, TICK_COUNT),
    [scale, firstStartMs, lastEndMs],
  );

  const revisionMarks = useMemo(
    () =>
      trace.revisions
        .filter((revision) => revision.offsetMs !== null)
        .map((revision) => ({
          kind: revision.kind,
          ms: revision.offsetMs as number,
          fraction: scale.fractionOf(revision.offsetMs as number),
        })),
    [trace.revisions, scale],
  );

  const body = trace.hasTimeAxis ? (
    <>
      {/* The ticks share the lane rows' grid so they line up with the tracks.
          They used to span the full container while the rows carried a label
          gutter, which quietly put every tick off by the gutter's width. */}
      {/* The ticks stay INSIDE the bordered box so they share its padding with
          the tracks. Outside it they drift by that padding, and back when the
          rows carried a label gutter they drifted by the gutter's width too. */}
      {/* One positioning context for the axis, the tracks and the markers, so
          a percentage means the same thing to all three. Anchoring a marker to
          the padded box instead would slide it by the padding — the same drift
          the ticks and the old label gutter both caused. */}
      <div className="relative rounded-lg border border-border p-2">
        <div className="relative">
          <div className="relative mb-1 h-4" data-testid="trace-axis">
            {ticks.map((tick) => (
              <span
                key={tick.fraction}
                className="absolute -translate-x-1/2 text-xs text-muted-foreground first:translate-x-0 last:-translate-x-full"
                style={{ left: `${tick.fraction * 100}%` }}
              >
                {formatMs(Math.round(tick.ms))}
              </span>
            ))}
          </div>
          {/* Where the axis changes rate. Without it the unevenly spaced labels
              are the only clue, and a reader who does not check them measures
              the tail against the head and gets it wrong by an order. */}
          {scale.breakFraction !== null && (
            <div
              data-testid="trace-axis-break"
              aria-hidden
              className="pointer-events-none absolute inset-y-0 z-10 border-l border-dashed border-foreground/40"
              style={{ left: `${scale.breakFraction * 100}%` }}
            />
          )}
          {/* Each time the body was replaced. The trace below belongs to the
              acknowledgement turn — the only turn there is — so without these
              the final report appears to have come from nowhere. */}
          {revisionMarks.map((mark) => (
            <div
              key={`${mark.kind}-${mark.fraction}`}
              data-testid="trace-revision-mark"
              title={`${mark.kind} · ${formatMs(Math.round(mark.ms))}`}
              className="pointer-events-none absolute top-0 z-10 h-3 w-px bg-trace-subagent"
              style={{ left: `${mark.fraction * 100}%` }}
            />
          ))}
          <div>
            {ordered.map(({ lane, depth }) => (
              <TraceLaneRow
                key={lane.id}
                lane={lane}
                depth={depth}
                scale={scale}
                selectedNodeId={selected?.id ?? null}
                highlightedNodeIds={highlightedNodeIds}
                siblingLabel={siblingLabels.get(lane.id)}
                onSelect={onSelect}
              />
            ))}
          </div>
        </div>
      </div>
      {/* Order matters: the two weakest pairs in the palette (inference↔citation,
          and tool↔citation) are kept apart so neither is read side by side. */}
      <div className="my-2 flex flex-wrap gap-3 text-xs text-muted-foreground">
        <LegendSwatch swatch="bg-trace-inference" label="추론 중" />
        {/* The pale shade always means the same thing — this lane's agent was
            waiting, not inferring. On a main turn that is orchestration or a
            sub-agent; on a sub-agent execution it is its own tool running. */}
        <LegendSwatch swatch="bg-trace-wait" label="대기 (추론 아님)" />
        <LegendSwatch swatch="bg-trace-tool" label="tool call" />
        <LegendSwatch swatch="bg-trace-tool-wait" label="병렬 배치" />
        <LegendSwatch swatch="bg-trace-subagent" label="sub-agent" />
        <LegendSwatch swatch="bg-trace-citation" label="citation worker" />
        <LegendSwatch swatch="bg-trace-error" label="error" />
        <LegendSwatch swatch="bg-trace-idle" label="미완료·중단" />
      </div>
      <TraceNodeList
        trace={trace}
        selectedNodeId={selected?.id ?? null}
        onSelect={onSelect}
      />
    </>
  ) : (
    <TraceSequence
      trace={trace}
      selectedNodeId={selected?.id ?? null}
      onSelect={onSelect}
    />
  );

  return body;
}
